import React, { useEffect, createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import type {
	spaceContextQuery$data,
	spaceContextQuery$variables,
} from './__generated__/spaceContextQuery.graphql.ts';

type SpaceContextValue = {
	spaceKey?: string;
	spaceId?: string;
	isAdmin: boolean;
};

const SpaceContext = createContext<SpaceContextValue | undefined>(undefined);

const spaceContextQuery = graphql`
	query spaceContextQuery($spaceKey: String) {
		space(key: $spaceKey) {
			id
			currentUser {
				isAdmin
			}
			operations {
				operation
				targetType
			}
		}
	}
`;

export const SpaceContextProvider = ({
	spaceKey,
	children,
}: {
	spaceKey?: string;
	children: ReactNode;
}) => {
	const { data, error } = useQueryInterop<spaceContextQuery$data, spaceContextQuery$variables>(
		spaceContextQuery,
		{
			variables: {
				spaceKey,
			},
			skip: !spaceKey,
			notifyOnNetworkStatusChange: true,
		},
	);

	useEffect(() => {
		if (error) {
			getMonitoringClient().submitError(error, { attribution: 'shareable-links' });
		}
	}, [error]);

	const value = useMemo<SpaceContextValue>(() => {
		const space = data?.space;
		return {
			spaceKey,
			spaceId: space?.id || undefined,
			isAdmin: space?.currentUser?.isAdmin ?? false,
		};
	}, [data, spaceKey]);

	return <SpaceContext.Provider value={value}>{children}</SpaceContext.Provider>;
};

export const useSpaceContext = () => {
	const context = useContext(SpaceContext);

	if (!context) {
		throw new Error('Missing SpaceContextProvider');
	}

	return context;
};
